import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom'; // Import useNavigate
import LoginPage from './pages/Login';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import PostsPage from './pages/PostsPage';
import CommentsPage from './pages/CommentsPage';
import EventsPage from './pages/EventsPage';
import CampaignsPage from './pages/CampaignsPage';
import FactChecksPage from './pages/FactChecksPage';
import CreatePostPage from './pages/CreatePostPage';
import { ChartPieIcon, UsersIcon, DocumentTextIcon, ChatBubbleLeftEllipsisIcon, CalendarIcon, MegaphoneIcon, CheckBadgeIcon, Bars3Icon, XMarkIcon, DocumentPlusIcon } from '@heroicons/react/24/solid';

const navLinkClasses = "flex items-center mt-2 py-2 px-6 text-gray-300 hover:bg-slate-700 hover:text-white rounded transition-colors duration-200";
const activeLinkClasses = "bg-slate-700 text-white";

// Helper function to check if user is authenticated
const isAuthenticated = () => {
  return localStorage.getItem('token') !== null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate(); // Use useNavigate hook
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login'); // Redirect to login if not authenticated
    }
  }, [navigate]); // Add navigate to dependency array

  return isAuthenticated() ? children : null; // Render children only if authenticated
};

const AdminLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="relative min-h-screen lg:flex bg-slate-100 font-sans">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 bg-gradient-to-b from-slate-800 to-slate-900 transform ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:flex lg:flex-col shadow-lg`}>
        <div className="flex items-center justify-center h-24 border-b border-slate-700">
          <h1 className="text-3xl font-bold text-white">JanChaitna</h1>
        </div>
        <nav className="flex-1 px-2 py-6 space-y-2 overflow-y-auto">
          <NavLink to="/" end className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <ChartPieIcon className="h-6 w-6" />
            <span className="mx-3">Dashboard</span>
          </NavLink>
          <NavLink to="/users" className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <UsersIcon className="h-6 w-6" />
            <span className="mx-3">Users</span>
          </NavLink>
          <NavLink to="/posts" className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <DocumentTextIcon className="h-6 w-6" />
            <span className="mx-3">Posts</span>
          </NavLink>
          <NavLink to="/create-post" className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <DocumentPlusIcon className="h-6 w-6" />
            <span className="mx-3">Create Post</span>
          </NavLink>
          <NavLink to="/comments" className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <ChatBubbleLeftEllipsisIcon className="h-6 w-6" />
            <span className="mx-3">Comments</span>
          </NavLink>
          <NavLink to="/events" className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <CalendarIcon className="h-6 w-6" />
            <span className="mx-3">Events</span>
          </NavLink>
          <NavLink to="/campaigns" className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <MegaphoneIcon className="h-6 w-6" />
            <span className="mx-3">Campaigns</span>
          </NavLink>
          <NavLink to="/fact-checks" className={({ isActive }) => isActive ? `${navLinkClasses} ${activeLinkClasses}` : navLinkClasses} onClick={() => setIsMenuOpen(false)}>
            <CheckBadgeIcon className="h-6 w-6" />
            <span className="mx-3">Fact-Checks</span>
          </NavLink>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="flex justify-between items-center px-4 lg:px-8 py-4 bg-white border-b border-slate-200 shadow-sm">
          <div className="lg:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              {isMenuOpen ? <XMarkIcon className="h-6 w-6 text-slate-700" /> : <Bars3Icon className="h-6 w-6 text-slate-700" />}
            </button>
          </div>
          <h2 className="text-xl lg:text-2xl font-semibold text-slate-900 hidden lg:block">Admin Dashboard</h2>
          <div>
            <button 
              onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }} 
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-100 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <DashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <UsersPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/posts"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <PostsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-post"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CreatePostPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/comments"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CommentsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <EventsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CampaignsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fact-checks"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <FactChecksPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
  );
}

export default App;